import { useState, useMemo } from "react";
import { getStepDisplayName } from "@/lib/stepUtils";
import { formatDate } from "@/lib/formatDate";
import { addWorkDays, subtractWorkDays } from "@/lib/workDays";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useProjects } from "@/hooks/useProjects";
import { useRuns } from "@/hooks/useRuns";
import { AppHeader } from "@/components/AppHeader";
import {
  Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from
"@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertCircle, ArrowRight, ArrowLeft, CalendarIcon, Check, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { format, parseISO } from "date-fns";
import type { ProductUnit, RunScale } from "@/types/chemistry";

const STEP_LABELS = ["Details", "Steps", "Notes & Review"] as const;

/** Sort steps by their label: numeric part first, then letter suffix */
function sortStepsByLabel(a: { label: string }, b: { label: string }) {
  const parse = (label: string) => {
    const match = label.replace(/^Step\s*/i, "").match(/^(\d+)(.*)$/);
    return match ? { num: parseInt(match[1], 10), suffix: match[2].toLowerCase() } : { num: 0, suffix: label.toLowerCase() };
  };
  const pa = parse(a.label);
  const pb = parse(b.label);
  if (pa.num !== pb.num) return pa.num - pb.num;
  return pa.suffix.localeCompare(pb.suffix);
}

const RunCreate = () => {
  const { projectId } = useParams<{projectId: string;}>();
  const navigate = useNavigate();
  const location = useLocation();
  const { getProject } = useProjects();
  const { createRun, getRunsForProject } = useRuns();
  const project = getProject(projectId!);
  const existingRuns = getRunsForProject(projectId!);
  const nextRunNumber = existingRuns.length + 1;

  const [wizardStep, setWizardStep] = useState(0);
  const [title, setTitle] = useState(() => {
    const planName = project?.name || "Untitled";
    return `${planName} - Run ${nextRunNumber}`;
  });
  const [compoundAmounts, setCompoundAmounts] = useState<Record<string, string>>({});
  const [productAmount, setProductAmount] = useState("");
  const [productUnit, setProductUnit] = useState<ProductUnit>("g");

  const [dateMode, setDateMode] = useState<"start" | "end">("start");
  const [selectedDate, setSelectedDate] = useState("");
  const [notes, setNotes] = useState("");
  const [defaultStepYield] = useState(80);
  const [selectedStepIds, setSelectedStepIds] = useState<string[]>([]);
  const [stepsInitialized, setStepsInitialized] = useState(false);
  const [stepOverrides, setStepOverrides] = useState<Record<string, {yield?: number;stepTimeDays?: number;}>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [notIncludedExpanded, setNotIncludedExpanded] = useState(false);

  // Get root steps (compounds) — only those with at least one selected step in their branch
  const compounds = useMemo(() => {
    if (!project) return [];
    const rootSteps = project.steps.filter(s => !s.parentId);
    const getDescendants = (id: string): string[] => {
      const children = project.steps.filter(s => s.parentId === id);
      return children.flatMap(c => [c.id, ...getDescendants(c.id)]);
    };
    return rootSteps
      .filter(root => {
        const branchIds = [root.id, ...getDescendants(root.id)];
        return branchIds.some(id => selectedStepIds.includes(id));
      })
      .map(s => ({ id: s.id, name: s.products?.[0]?.name || s.name || "Unknown compound" }));
  }, [project?.steps, selectedStepIds]);

  // Derive a single productAmount from the first compound for backward compat
  const firstCompoundAmount = useMemo(() => {
    const vals = Object.values(compoundAmounts).filter(v => v && !isNaN(parseFloat(v)));
    return vals.length > 0 ? vals[0] : "";
  }, [compoundAmounts]);

  if (project && !stepsInitialized) {
    const fromCanvas = (location.state as any)?.selectedStepIds;
    setSelectedStepIds(
      Array.isArray(fromCanvas) && fromCanvas.length > 0 ?
      fromCanvas :
      project.steps.map((s) => s.id)
    );
    setStepsInitialized(true);
  }

  // Compute per-step date ranges based on scheduling
  const stepDateRanges = useMemo(() => {
    if (!selectedDate || selectedStepIds.length === 0 || !project) return {};
    const sortedSteps = [...project.steps]
      .filter((s) => selectedStepIds.includes(s.id))
      .sort(sortStepsByLabel);

    const ranges: Record<string, { start: Date; end: Date }> = {};

    if (dateMode === "start") {
      let cursor = parseISO(selectedDate);
      for (const step of sortedSteps) {
        const days = stepOverrides[step.id]?.stepTimeDays || 0;
        const stepStart = new Date(cursor);
        const stepEnd = days > 0 ? addWorkDays(cursor, days) : new Date(cursor);
        ranges[step.id] = { start: stepStart, end: stepEnd };
        if (days > 0) cursor = stepEnd;
      }
    } else {
      let cursor = parseISO(selectedDate);
      for (const step of [...sortedSteps].reverse()) {
        const days = stepOverrides[step.id]?.stepTimeDays || 0;
        const stepEnd = new Date(cursor);
        const stepStart = days > 0 ? subtractWorkDays(cursor, days) : new Date(cursor);
        ranges[step.id] = { start: stepStart, end: stepEnd };
        if (days > 0) cursor = stepStart;
      }
    }

    return ranges;
  }, [selectedDate, dateMode, selectedStepIds, stepOverrides, project?.steps]);

  const computedOtherDate = useMemo(() => {
    if (!selectedDate || selectedStepIds.length === 0) return null;
    const totalDays = selectedStepIds.reduce((sum, id) => sum + (stepOverrides[id]?.stepTimeDays || 0), 0);
    if (totalDays === 0) return null;
    if (dateMode === "start") {
      return addWorkDays(parseISO(selectedDate), totalDays);
    } else {
      return subtractWorkDays(parseISO(selectedDate), totalDays);
    }
  }, [selectedDate, dateMode, selectedStepIds, stepOverrides]);

  if (!project) {
    return (
      <div className="min-h-screen bg-background">
        <AppHeader />
        <div className="flex items-center justify-center py-20">
          <p className="text-muted-foreground">Compound not found</p>
        </div>
      </div>);

  }

  const validateStep0 = () => {
    const errs: Record<string, string> = {};
    if (!title.trim()) errs.title = "Run title is required";
    const hasAnyAmount = Object.values(compoundAmounts).some(v => {
      const n = parseFloat(v);
      return v && !isNaN(n) && n > 0;
    });
    if (!hasAnyAmount) errs.productAmount = "Enter a product amount for at least one compound";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateStep1 = () => {
    const errs: Record<string, string> = {};
    if (selectedStepIds.length === 0) errs.steps = "Select at least one step";
    if (!selectedDate) errs.date = "Please select a date";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleNext = () => {
    if (wizardStep === 0 && !validateStep0()) return;
    if (wizardStep === 1 && !validateStep1()) return;
    setErrors({});
    setWizardStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setErrors({});
    setWizardStep((prev) => prev - 1);
  };

  const handleCreate = () => {
    const totalAmount = Object.values(compoundAmounts).reduce((sum, v) => {
      const n = parseFloat(v);
      return sum + (isNaN(n) ? 0 : n);
    }, 0);
    const run = createRun(
      project.id,
      title.trim(),
      totalAmount || parseFloat(firstCompoundAmount) || 0,
      productUnit,
      "lab" as RunScale,
      project,
      {
        deadline: dateMode === "end" ? selectedDate : undefined,
        startDate: dateMode === "start" ? selectedDate : undefined,
        notes,
        assumedYield: computedOverallYield,
        selectedStepIds,
        stepOverrides: Object.keys(stepOverrides).length > 0 ? stepOverrides : undefined,
        compoundAmounts: Object.fromEntries(
          Object.entries(compoundAmounts)
            .filter(([, v]) => v && !isNaN(parseFloat(v)) && parseFloat(v) > 0)
            .map(([k, v]) => [k, parseFloat(v)])
        ),
      }
    );
    navigate(`/project/${project.id}/run/${run.id}`);
  };

  const updateStepOverride = (stepId: string, field: "yield" | "stepTimeDays", value: string) => {
    setStepOverrides((prev) => {
      const current = prev[stepId] || {};
      const numVal = value === "" ? undefined : parseFloat(value);
      const updated = { ...current, [field]: isNaN(numVal as number) ? undefined : numVal };
      if (updated.yield === undefined && updated.stepTimeDays === undefined) {
        const { [stepId]: _, ...rest } = prev;
        return rest;
      }
      return { ...prev, [stepId]: updated };
    });
  };

  const computedOverallYield = selectedStepIds.reduce((acc, sid) => {
    const stepYield = stepOverrides[sid]?.yield ?? defaultStepYield;
    return acc * (stepYield / 100);
  }, 1) * 100;
  const displayOverallYield = selectedStepIds.length > 0 ? Math.round(computedOverallYield * 10) / 10 : defaultStepYield;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader />
      <main className="mx-auto max-w-3xl px-6 py-6">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink href="/">Synthesis Plans</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbLink href={`/project/${project.id}`}>{project.name}</BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>New Run</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <h1 className="text-2xl font-bold tracking-tight mb-6">Create New Run</h1>

        {/* Wizard step indicator */}
        <div className="flex items-center gap-2 mb-8">
          {STEP_LABELS.map((label, i) =>
          <div key={label} className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium ${
              i < wizardStep ? "bg-primary text-primary-foreground" :
              i === wizardStep ? "bg-primary text-primary-foreground" :
              "bg-muted text-muted-foreground"}`
              }>
                  {i < wizardStep ? <Check className="h-3.5 w-3.5" /> : i + 1}
                </div>
                <span className={`text-sm ${i === wizardStep ? "font-medium text-foreground" : "text-muted-foreground"}`}>
                  {label}
                </span>
              </div>
              {i < STEP_LABELS.length - 1 &&
            <div className={`w-8 h-px ${i < wizardStep ? "bg-primary" : "bg-border"}`} />
            }
            </div>
          )}
        </div>

        {/* Step 1: Details */}
        {wizardStep === 0 &&
        <div className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Run Title *</Label>
              <Input
              id="title"
              placeholder={`${project.targetCompound} — Run 1`}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className={errors.title ? "border-destructive" : ""} />
            
              {errors.title && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.title}</p>}
            </div>

            <div className="space-y-2">
              <Label>Target Compounds *</Label>
              <p className="text-xs text-muted-foreground">Set a product amount for each compound.</p>
              <div className="rounded-lg border bg-muted/40 divide-y divide-border">
                {compounds.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 px-3 py-2.5">
                    <span className="flex-1 text-sm font-medium truncate">{c.name}</span>
                    <div className="flex gap-1.5">
                      <Input
                        type="text"
                        inputMode="decimal"
                        placeholder="0"
                        value={compoundAmounts[c.id] ?? ""}
                        onChange={(e) => {
                          const v = e.target.value;
                          if (v === "" || /^\d*\.?\d*$/.test(v)) {
                            setCompoundAmounts(prev => ({ ...prev, [c.id]: v }));
                          }
                        }}
                        className="w-24 h-8 text-sm [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />
                      <Select value={productUnit} onValueChange={(v) => setProductUnit(v as ProductUnit)}>
                        <SelectTrigger className="w-16 h-8 text-sm"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {["mg", "g", "kg", "mL", "L"].map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))}
                {compounds.length === 0 && (
                  <div className="px-3 py-4 text-xs text-muted-foreground/60 text-center italic">No compounds defined</div>
                )}
              </div>
              {errors.productAmount && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.productAmount}</p>}
            </div>
          </div>
        }

        {/* Step 2: Steps & Scheduling */}
        {wizardStep === 1 &&
        <div className="space-y-4">
            {/* Scheduling */}
            <div className="space-y-2">
              <Label>Scheduling *</Label>
              <p className="text-xs text-muted-foreground">Set either a start date or a deadline. The other will be calculated from step workdays.</p>
              <div className="flex items-center gap-3">
                <div className="relative inline-flex items-center rounded-md border border-input p-0.5 h-8">
                  <div
                    className="absolute top-0.5 bottom-0.5 rounded bg-primary shadow-sm transition-all duration-300 ease-in-out"
                    style={{
                      left: dateMode === "start" ? "2px" : "50%",
                      width: "calc(50% - 2px)",
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => { setDateMode("start"); setSelectedDate(""); }}
                    className={cn(
                      "relative z-10 px-2.5 py-1 text-xs font-medium rounded transition-colors duration-300",
                      dateMode === "start" ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Start Date
                  </button>
                  <button
                    type="button"
                    onClick={() => { setDateMode("end"); setSelectedDate(""); }}
                    className={cn(
                      "relative z-10 px-2.5 py-1 text-xs font-medium rounded transition-colors duration-300",
                      dateMode === "end" ? "text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                    )}
                  >
                    Deadline
                  </button>
                </div>
                <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn("w-[200px] h-8 justify-start text-left font-normal text-sm", !selectedDate && "text-muted-foreground", errors.date && "border-destructive")}
                    >
                      <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                      {selectedDate ? format(parseISO(selectedDate), "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={selectedDate ? parseISO(selectedDate) : undefined}
                      onSelect={(d) => { setSelectedDate(d ? format(d, "yyyy-MM-dd") : ""); setDatePickerOpen(false); }}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </div>
              {errors.date && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.date}</p>}
              {computedOtherDate && selectedDate && (
                <p className="text-xs text-muted-foreground">
                  Calculated {dateMode === "start" ? "deadline" : "start date"}: <span className="font-medium text-foreground">{format(computedOtherDate, "PPP")}</span>
                </p>
              )}
            </div>

            {/* Steps */}
            <div className="space-y-2">
              <Label>Steps to Include *</Label>
              <p className="text-xs text-muted-foreground">Configure per-step yield and time estimates.</p>
            </div>

            <div className="rounded-lg border bg-card">
              <div className="flex items-center gap-3 px-3 py-2 bg-muted/30 border-b">
                <span className="text-xs font-medium text-muted-foreground flex-1">
                  Included ({selectedStepIds.length})
                </span>
                <span className="text-xs font-medium text-muted-foreground w-20 text-center">Yield %</span>
                <span className="text-xs font-medium text-muted-foreground w-20 text-center">Workdays</span>
                {selectedDate && <span className="text-xs font-medium text-muted-foreground w-[120px] text-center">Date Range</span>}
              </div>
              {project.steps.filter((s) => selectedStepIds.includes(s.id)).length === 0 &&
                <div className="px-3 py-4 text-xs text-muted-foreground/60 text-center italic">No steps selected</div>
              }
              {[...project.steps].filter((s) => selectedStepIds.includes(s.id)).sort(sortStepsByLabel).map((step) => {
                const override = stepOverrides[step.id];
                const dateRange = stepDateRanges[step.id];
                const hasDays = (override?.stepTimeDays ?? 0) > 0;
                return (
                  <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 border-b last:border-b-0 bg-accent/30">
                    <Checkbox
                      id={`step-${step.id}`}
                      checked={true}
                      onCheckedChange={() => setSelectedStepIds((prev) => prev.filter((id) => id !== step.id))}
                    />
                    <label htmlFor={`step-${step.id}`} className="flex-1 cursor-pointer min-w-0">
                      <span className="text-sm font-medium">{getStepDisplayName(step)}</span>
                      {step.notes && <span className="text-xs text-muted-foreground ml-2 truncate">— {step.notes}</span>}
                    </label>
                    <Input
                      type="number" min="1" max="100"
                      placeholder={String(defaultStepYield)}
                      value={override?.yield ?? ""}
                      onChange={(e) => updateStepOverride(step.id, "yield", e.target.value)}
                      className="w-20 h-7 text-xs text-center"
                    />
                    <Input
                      type="number" min="0"
                      placeholder="—"
                      value={override?.stepTimeDays ?? ""}
                      onChange={(e) => updateStepOverride(step.id, "stepTimeDays", e.target.value)}
                      className="w-20 h-7 text-xs text-center"
                    />
                    {selectedDate && (
                      <span className="w-[120px] text-xs text-muted-foreground text-center">
                        {dateRange && hasDays
                          ? `${format(dateRange.start, "MMM d")} – ${format(dateRange.end, "MMM d")}`
                          : "—"
                        }
                      </span>
                    )}
                  </div>
                );
              })}
            </div>

            {project.steps.filter((s) => !selectedStepIds.includes(s.id)).length > 0 &&
              <div className="rounded-lg border bg-card">
                <button
                  type="button"
                  onClick={() => setNotIncludedExpanded((prev) => !prev)}
                  className="flex items-center gap-2 px-3 py-2 w-full text-left bg-muted/30 hover:bg-muted/40 transition-colors"
                >
                  {notIncludedExpanded ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" /> : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                  <span className="text-xs font-medium text-muted-foreground">
                    Not included ({project.steps.filter((s) => !selectedStepIds.includes(s.id)).length})
                  </span>
                </button>
                {notIncludedExpanded && [...project.steps].filter((s) => !selectedStepIds.includes(s.id)).sort(sortStepsByLabel).map((step) =>
                  <div key={step.id} className="flex items-center gap-3 px-3 py-2.5 border-t bg-accent/30">
                    <Checkbox
                      id={`step-ex-${step.id}`}
                      checked={false}
                      onCheckedChange={() => setSelectedStepIds((prev) => [...prev, step.id])}
                    />
                    <label htmlFor={`step-ex-${step.id}`} className="flex-1 cursor-pointer min-w-0">
                      <span className="text-sm font-medium text-muted-foreground">{getStepDisplayName(step)}</span>
                    </label>
                  </div>
                )}
              </div>
            }

            {errors.steps && <p className="text-xs text-destructive flex items-center gap-1"><AlertCircle className="h-3 w-3" />{errors.steps}</p>}

            <div className="space-y-1 pt-2">
              <Label>Overall Yield</Label>
              <div className="h-10 rounded-md border border-input bg-muted/40 px-3 py-2 text-sm font-medium w-32">
                {displayOverallYield}%
              </div>
              <p className="text-xs text-muted-foreground">Calculated from per-step yields (default {defaultStepYield}%)</p>
            </div>
          </div>
        }

        {/* Step 3: Notes & Review */}
        {wizardStep === 2 &&
        <div className="space-y-6">
            {/* Review summary */}
            <div className="space-y-3">
              <Label>Review</Label>
              <div className="rounded-lg border divide-y">
                <div className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                  <span className="text-sm text-muted-foreground">Title</span>
                  <span className="text-sm font-medium">{title}</span>
                </div>
                <div className="px-4 py-2.5 bg-accent/30">
                  <span className="text-sm text-muted-foreground">Target Compounds</span>
                  <div className="mt-1.5 space-y-1">
                    {compounds.map((c) => {
                      const amt = compoundAmounts[c.id];
                      return (
                        <div key={c.id} className="flex items-center justify-between">
                          <span className="text-sm font-medium">{c.name}</span>
                          <span className="text-sm text-muted-foreground">{amt || "—"} {amt ? productUnit : ""}</span>
                        </div>
                      );
                    })}
                    {compounds.length === 0 && <span className="text-sm text-muted-foreground">—</span>}
                  </div>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                  <span className="text-sm text-muted-foreground">{dateMode === "start" ? "Start Date" : "Deadline"}</span>
                  <span className="text-sm">{formatDate(selectedDate)}</span>
                </div>
                {computedOtherDate && (
                  <div className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                    <span className="text-sm text-muted-foreground">{dateMode === "start" ? "Calculated Deadline" : "Calculated Start"}</span>
                    <span className="text-sm">{format(computedOtherDate, "PPP")}</span>
                  </div>
                )}
                <div className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                  <span className="text-sm text-muted-foreground">Steps</span>
                  <span className="text-sm font-medium">{selectedStepIds.length} of {project.steps.length}</span>
                </div>
                <div className="flex items-center justify-between px-4 py-2.5 bg-accent/30">
                  <span className="text-sm text-muted-foreground">Overall Yield</span>
                  <span className="text-sm font-medium">{displayOverallYield}%</span>
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
              id="notes"
              placeholder="Additional context for this run..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3} />
            
            </div>
          </div>
        }

        {/* Navigation */}
        <div className="flex justify-between pt-8 border-t mt-8">
          <div>
            {wizardStep === 0 ?
            <Button variant="outline" onClick={() => navigate(`/project/${project.id}`)}>Cancel</Button> :

            <Button variant="outline" onClick={handleBack} className="gap-1.5">
                <ArrowLeft className="h-4 w-4" /> Back
              </Button>
            }
          </div>
          <div>
            {wizardStep < 2 ?
            <Button onClick={handleNext} className="gap-1.5">
                Next <ArrowRight className="h-4 w-4" />
              </Button> :

            <Button onClick={handleCreate} className="gap-1.5">
                Create Run & View BOM <ArrowRight className="h-4 w-4" />
              </Button>
            }
          </div>
        </div>
      </main>
    </div>);

};

export default RunCreate;